import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";

class SceneTransitionController {
  constructor(container, dimensions) {
    this.container = container;
    this.dimensions = dimensions;
    this.transitionSpine = null;
    this.isPlaying = false;
    this.midTransitionCallback = null;
    this.completeCallback = null;
    this.initialized = false;
  }

  /**
   * Initialize the transition spine animation
   */
  async initializeSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "transition_eye-skeleton";
      const atlasAlias = "transition_eye-atlas";

      // Check if assets are loaded
      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return false;
      }

      // Create spine instance
      this.transitionSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      // Verify spine was created properly
      if (
        !this.transitionSpine ||
        !this.transitionSpine.skeleton ||
        !this.transitionSpine.state
      ) {
        return false;
      }

      // Verify skeleton has required data
      if (
        !this.transitionSpine.skeleton.data ||
        !this.transitionSpine.skeleton.data.animations
      ) {
        return false;
      }

      // Add to container FIRST (before structure initialization)
      this.container.addChild(this.transitionSpine);

      // Set initial properties - CRITICAL: use 0.001 not 0
      this.transitionSpine.alpha = 0.001; // Hidden initially but in render pipeline
      this.transitionSpine.visible = true;

      // CRITICAL: Initialize spine structure to populate bones/slots/attachments
      // This compiles shaders during loading
      this.initializeSpineStructure();

      // Apply initial position and scale (only if dimensions are ready)
      if (this.dimensions) {
        this.applyScaleAndPosition();
      }

      this.initialized = true;

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   * Minimal requirement: set one animation + update once
   */
  initializeSpineStructure() {
    if (!this.transitionSpine || !this.transitionSpine.state) {
      return;
    }

    try {
      // Set any animation to force spine to load bones/slots/attachments
      const animations = this.transitionSpine.skeleton.data.animations;
      if (animations.length > 0) {
        const firstAnimation = animations[0].name;
        this.transitionSpine.state.setAnimation(0, firstAnimation, false);
        this.transitionSpine.state.update(0.016); // Update one frame
        this.transitionSpine.skeleton.updateWorldTransform(); // Populate hierarchy
      }

      // Keep at alpha = 0.001 so PixiJS continues rendering it
      // This keeps GPU buffers hot after skeleton is populated
      this.transitionSpine.alpha = 0.001;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Apply scaling and positioning to match screen dimensions
   * Respects 1920x1080 maximum canvas size
   */
  applyScaleAndPosition() {
    if (!this.transitionSpine || !this.transitionSpine.skeleton) {
      return;
    }

    try {
      // Update skeleton to ensure bounds are accurate
      this.transitionSpine.skeleton.updateWorldTransform();

      // Get native spine dimensions from bounds
      const bounds = this.transitionSpine.getLocalBounds();
      const nativeWidth = bounds.width;
      const nativeHeight = bounds.height;

      if (nativeWidth <= 0 || nativeHeight <= 0) {
        // Fallback: use a default scale if bounds are invalid
        this.transitionSpine.scale.set(1, 1);
        this.transitionSpine.position.set(
          this.dimensions.width / 2,
          this.dimensions.height / 2
        );
        return;
      }

      // Cap dimensions at 1920x1080 (same as game canvas)
      const MAX_WIDTH = 1920;
      const MAX_HEIGHT = 1080;
      const targetWidth = Math.min(this.dimensions.width, MAX_WIDTH);
      const targetHeight = Math.min(this.dimensions.height, MAX_HEIGHT);

      // Calculate scale to cover the target area (capped dimensions)
      const scaleX = targetWidth / nativeWidth;
      const scaleY = targetHeight / nativeHeight;
      const scale = Math.max(scaleX, scaleY); // Use max to ensure full coverage

      this.transitionSpine.scale.set(scale, scale);

      // Center the spine on screen (not on capped dimensions)
      this.transitionSpine.position.set(
        this.dimensions.width / 2,
        this.dimensions.height / 2
      );
    } catch (error) {
      // Fallback positioning
      this.transitionSpine.scale.set(1, 1);
      this.transitionSpine.position.set(
        this.dimensions.width / 2,
        this.dimensions.height / 2
      );
    }
  }

  /**
   * Play the transition animation
   * @param {Function} midTransitionCallback - Called at the midpoint (when screen is fully covered)
   * @param {Function} completeCallback - Called when animation completes
   */
  playTransition(midTransitionCallback, completeCallback) {
    if (!this.transitionSpine || this.isPlaying) {
      return;
    }

    this.isPlaying = true;
    this.midTransitionCallback = midTransitionCallback;
    this.completeCallback = completeCallback;

    // Fade in spine from 0.001 to 1 (smooth transition, no shader recompilation)
    this.transitionSpine.alpha = 1;

    // Clear any existing listeners
    this.transitionSpine.state.clearListeners();

    // Play the transition animation (should be named "transition" or similar)
    // Check available animations
    const animations = this.transitionSpine.skeleton.data.animations;
    const animationName = animations.length > 0 ? animations[0].name : null;

    if (!animationName) {
      this.finishTransition();
      return;
    }

    // Set the animation
    this.transitionSpine.state.setAnimation(0, animationName, false);

    // Get animation duration to calculate midpoint
    const animation =
      this.transitionSpine.skeleton.data.findAnimation(animationName);
    const duration = animation ? animation.duration : 3.3333; // Default to 3.3333s if not found

    // Schedule midpoint callback (when eye is fully closed)
    // Based on the animation structure, this should be around 50% of the duration
    const midpointTime = duration * 0.5;

    setTimeout(() => {
      if (this.isPlaying && this.midTransitionCallback) {
        this.midTransitionCallback();
      }
    }, midpointTime * 1000); // Convert to milliseconds

    // Add completion listener
    this.transitionSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === animationName && this.isPlaying) {
          this.finishTransition();
        }
      },
    });
  }

  /**
   * Finish the transition and cleanup
   */
  finishTransition() {
    if (!this.isPlaying) {
      return;
    }

    this.isPlaying = false;

    // CRITICAL: Hide spine with 0.001 alpha (not 0) to keep in render pipeline
    if (this.transitionSpine) {
      this.transitionSpine.alpha = 0.001; // Keep GPU buffers hot
      this.transitionSpine.state.clearListeners();
      this.transitionSpine.state.clearTracks();
    }

    // Call completion callback
    if (this.completeCallback) {
      this.completeCallback();
      this.completeCallback = null;
    }

    this.midTransitionCallback = null;
  }

  /**
   * Cleanup and destroy
   */
  destroy() {
    if (this.transitionSpine) {
      this.transitionSpine.state.clearListeners();
      this.transitionSpine.state.clearTracks();
      this.transitionSpine.destroy();
      this.transitionSpine = null;
    }

    this.midTransitionCallback = null;
    this.completeCallback = null;
    this.isPlaying = false;
    this.initialized = false;
  }

  /**
   * Update dimensions (for window resize)
   * CRITICAL: Always update spine positioning whenever dimensions change, even when hidden
   * This ensures the spine is always positioned correctly, preventing jumps on first appearance
   */
  updateDimensions(dimensions) {
    // Check if dimensions actually changed
    const dimensionsChanged =
      !this.dimensions ||
      this.dimensions.width !== dimensions?.width ||
      this.dimensions.height !== dimensions?.height;

    if (!dimensionsChanged) {
      return;
    }

    this.dimensions = dimensions;

    // CRITICAL: Always update spine positioning whenever dimensions change, even when hidden
    // This ensures the spine is always positioned correctly, preventing jumps on first appearance
    if (this.transitionSpine && this.dimensions) {
      this.applyScaleAndPosition();
    }
  }
}

export default SceneTransitionController;
